import React, { useState, useEffect } from 'react';
import {
	People,
	TrendingUp,
	AccessTime,
	LocalFireDepartment,
	PieChart,
	AddCircleOutline
} from '@mui/icons-material';
import PlaylistEditModal from '../playlist/studio-playlist/PlaylistEditModal';
import axiosConfig from '../../data/utils/axiosConfigInstance';
import './StudioOverviewMetrics.css';

// const MOCK_ANALYTICS_DATA = {
// 	watchHours: '54.2',
// 	watchTimeGrowth: 12,
// 	activeInstructors30Days: 4,
// 	totalSeats: 5,
// 	activeInstructorsPercent: 80,
// 	teamActivityScore: 'HIGH',
// 	lessonUsageGrowth: 39,
// 	categories: [
// 		{ name: 'Classes', count: 41, percentage: 41, color: '#ff5f57' },
// 		{ name: 'Exercises', count: 17, percentage: 17, color: '#4da3ff' },
// 		{ name: 'Lessons', count: 24, percentage: 24, color: '#f9a826' },
// 		{ name: 'Across The Floor', count: 18, percentage: 18, color: '#39d98a' }
// 	],
// 	trendingTags: [
// 		{ name: 'Strength', views: 128, percentage: 100, color: '#2dd4bf' },
// 		{ name: 'Jumps', views: 92, percentage: 72, color: '#38bdf8' },
// 		{ name: 'Legs', views: 87, percentage: 68, color: '#f59e0b' },
// 		{ name: 'Breakdown', views: 61, percentage: 48, color: '#39d98a' }
// 	]
// };

const StudioOverviewMetrics = () => {
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const [metricsData, setMetricsData] = useState(null);

	useEffect(() => {
		const fetchMetrics = async () => {
			setLoading(true);
			try {
				const response = await axiosConfig.get('/studio/analytics-overview');
				if (response.data?.success) {
					setMetricsData(response.data.data);
				}
			} catch (error) {
				console.error('Error fetching studio metrics:', error);
			} finally {
				setLoading(false);
			}
		};
		fetchMetrics();
	}, []);

	if (loading) {
		return (
			<section id="studio-overview-section" className="account-v2-panel account-v2-studio-metrics-panel">
				<div className="account-v2-panel__header metrics-header-with-action">
					<div className="metrics-header-left">
						<div className="metrics-panel-title-row">
							<PieChart className="metrics-header-icon" />
							<h2>STUDIO HUB ADMIN OVERVIEW METRICS</h2>
						</div>
						<p>Real-time analytics and utilization insights for your studio's team activity.</p>
					</div>
					<button
						type="button"
						className="create-playlist-btn"
						onClick={() => setShowCreateModal(true)}
					>
						<AddCircleOutline className="btn-icon" />
						<span>CREATE STUDIO PLAYLIST</span>
					</button>
				</div>
				<div className="metrics-loading-state">
					<div className="spinner"></div>
					<p>Loading studio metrics...</p>
				</div>
				<PlaylistEditModal
					showModal={showCreateModal}
					setModalShow={setShowCreateModal}
					handleClose={() => setShowCreateModal(false)}
					editPlaylistID=""
				/>
			</section>
		);
	}

	// Derived metrics from fetched data
	const displayMetrics = metricsData || {
		watchHours: '0.0',
		watchTimeGrowth: 0,
		activeInstructors30Days: 0,
		totalSeats: 1,
		activeInstructorsPercent: 0,
		teamActivityScore: 'LOW',
		lessonUsageGrowth: 0,
		categories: [],
		trendingTags: []
	};
	const watchHours = displayMetrics.watchHours || '0.0';
	const estimatedTotalViews = Math.max(Math.round((Number.parseFloat(watchHours) || 0) * 6), 1);
	const watchTimeGrowth = displayMetrics.watchTimeGrowth ?? 0;
	const activeInstructors30Days = displayMetrics.activeInstructors30Days ?? 0;
	const totalSeatsMock = displayMetrics.totalSeats ?? 1;
	const activeInstructorsPercent = displayMetrics.activeInstructorsPercent ?? 0;
	const teamActivityScore = displayMetrics.teamActivityScore || 'LOW';
	const lessonUsageGrowth = displayMetrics.lessonUsageGrowth ?? 0;
	const categories = displayMetrics.categories || [];
	const trendingTags = displayMetrics.trendingTags || [];
	let assignedCategoryViews = 0;
	const categoriesWithEstimatedViews = categories.map((category, index) => {
		const isLastCategory = index === categories.length - 1;
		const estimatedViews = isLastCategory
			? Math.max(estimatedTotalViews - assignedCategoryViews, 1)
			: Math.max(Math.round((estimatedTotalViews * category.percentage) / 100), 1);
		assignedCategoryViews += estimatedViews;

		return {
			...category,
			views: estimatedViews
		};
	});

	return (
		<section id="studio-overview-section" className="account-v2-panel account-v2-studio-metrics-panel">
			<div className="account-v2-panel__header metrics-header-with-action">
				<div className="metrics-header-left">
					<div className="metrics-panel-title-row">
						<PieChart className="metrics-header-icon" />
						<h2>STUDIO HUB ADMIN OVERVIEW METRICS</h2>
					</div>
					<p>Real-time analytics and utilization insights for your studio's team activity.</p>
				</div>
				<button
					type="button"
					className="create-playlist-btn"
					onClick={() => setShowCreateModal(true)}
				>
					<AddCircleOutline className="btn-icon" />
					<span>CREATE STUDIO PLAYLIST</span>
				</button>
			</div>

			<div className="metrics-dashboard-grid metrics-dashboard-grid--relocated">
				{/* Key metrics grid (takes up first column on desktop now) */}
				<div className="metrics-subgrid">
					{/* Card 1: Total Watch Time */}
					<div className="metrics-card summary-stat-card">
						<div className="stat-card-icon-wrap time-icon">
							<AccessTime />
						</div>
						<div className="stat-card-data">
							<span className="stat-label">Total Watch Time (Monthly)</span>
							<div className="stat-val-row">
								<span className="stat-number">{watchHours} hrs</span>
								<span className="stat-trend trend-positive">
									<TrendingUp style={{ fontSize: '16px' }} /> {watchTimeGrowth >= 0 ? `+${watchTimeGrowth}%` : `${watchTimeGrowth}%`}
								</span>
							</div>
							<p className="stat-desc">Combined sub-users watch time vs previous month.</p>
						</div>
					</div>

					{/* Card 2: Monthly Active Instructors */}
					<div className="metrics-card summary-stat-card">
						<div className="stat-card-icon-wrap users-icon">
							<People />
						</div>
						<div className="stat-card-data">
							<span className="stat-label">Monthly Active Instructors</span>
							<div className="stat-val-row">
								<span className="stat-number">{activeInstructors30Days} <span className="stat-denom">/ {totalSeatsMock}</span></span>
								<span className="stat-badge">{activeInstructorsPercent}% Active</span>
							</div>
							<p className="stat-desc">Instructors (sub-users) active within the last 30 days.</p>
						</div>
					</div>

					{/* Card 3: Team Activity Score */}
					<div className="metrics-card summary-stat-card">
						<div className="stat-card-icon-wrap score-icon">
							<LocalFireDepartment />
						</div>
						<div className="stat-card-data">
							<span className="stat-label">Team Activity Score</span>
							<div className="stat-val-row">
								<span className="stat-number score-high" style={{ color: teamActivityScore === 'HIGH' ? '#ff9f43' : teamActivityScore === 'MEDIUM' ? '#74c8f7' : '#8892b0' }}>
									{teamActivityScore}
								</span>
							</div>
							<p className="stat-desc">Based on logins, views, and active adoption trends.</p>
						</div>
					</div>
				</div>

				{/* Growth Comparison (Monthly) */}
				<div className="metrics-card growth-comparison-card">
					<h3>Growth Comparison (Monthly)</h3>
					<p className="card-subtitle">Month-over-month engagement depth and return on investment.</p>
					<div className="growth-metrics-row">
						<div className="growth-metric-box">
							<span className="box-title">Watch Time Growth</span>
							<div className="box-value green-text">{watchTimeGrowth >= 0 ? `+${watchTimeGrowth}%` : `${watchTimeGrowth}%`}</div>
							<p className="box-desc">Growth in all Studio users Watch Time.</p>
						</div>
						<div className="growth-metric-box">
							<span className="box-title">Studio Hub Usage</span>
							<div className="box-value green-text">{lessonUsageGrowth >= 0 ? `+${lessonUsageGrowth}%` : `${lessonUsageGrowth}%`}</div>
							<p className="box-desc">Growth in Studio Hub usage.</p>
						</div>
					</div>
				</div>

				{/* Category usage Breakdown card */}
				<div className="metrics-card category-breakdown-card">
					<h3>Content Category Usage Breakdown</h3>
					<p className="card-subtitle">Shows how your team uses the platform across content types.</p>
					<div className="category-list">
						{categoriesWithEstimatedViews.length > 0 ? (
							categoriesWithEstimatedViews.map((cat) => (
								<div className="category-progress-item" key={cat.name}>
									<div className="category-meta">
										<span className="cat-name">{cat.name}</span>
										<div className="cat-details">
											<span className="cat-count">{cat.views} views</span>
											<span className="cat-pct">{cat.percentage}%</span>
										</div>
									</div>
									<div className="category-progress-track">
										<div
											className="category-progress-bar"
											style={{
												width: `${cat.percentage}%`,
												background: cat.color
											}}
										></div>
									</div>
								</div>
							))
						) : (
							<p className="stat-desc">No category usage data recorded yet.</p>
						)}
					</div>
				</div>

				{/* Trending Tags Card */}
				<div className="metrics-card trending-tags-card">
					<h3>Trending Tags This Month</h3>
					<p className="card-subtitle">Most watched focus areas by your team this month.</p>
					<div className="trending-tags-list">
						{trendingTags.length > 0 ? (
							trendingTags.map((tag) => (
								<div className="tag-trend-item" key={tag.name}>
									<div className="tag-trend-info">
										<span className="tag-pill">{tag.name}</span>
										<span className="tag-views">{tag.views} views</span>
									</div>
									<div className="tag-trend-bar-bg">
										<div
											className="tag-trend-bar-fill"
											style={{
												width: `${tag.percentage}%`,
												backgroundColor: tag.color
											}}
										></div>
									</div>
								</div>
							))
						) : (
							<p className="stat-desc">No trending tags recorded yet.</p>
						)}
					</div>
				</div>
			</div>

			<PlaylistEditModal
				showModal={showCreateModal}
				setModalShow={setShowCreateModal}
				handleClose={() => setShowCreateModal(false)}
				editPlaylistID=""
			/>
		</section>
	);
};

export default StudioOverviewMetrics;
